class PredictiveSearch extends HTMLElement {
  cachedResults = {};
  input = undefined;
  predictiveSearchResults = undefined;
  modal = undefined;
  section = 'predictive-search';

  constructor() {
    super();
  }

  /**
   * Connected callback.
   */
  connectedCallback() {
    this.isMobile = this.hasAttribute('data-mobile');
    this.input = this.isMobile
      ? this.querySelector('.js-trigger-form input')
      : this.querySelector('.js-search-modal [role="combobox"]');
    this.predictiveSearchResults = this.querySelector(
      'predictive-search-results',
    );
    this.modal = this.querySelector('.js-search-modal');
    this.setupEventListeners();

    if (this.isMobile) {
      this.section = 'predictive-search-mobile';
    }
  }

  /**
   * Setup event listeners.
   */
  setupEventListeners() {
    const trigger = this.querySelector('.js-trigger-form input');
    const form = this.querySelector('.js-modal-form');
    const close = this.isMobile
      ? this.nextElementSibling
      : this.querySelector('.js-search-modal .js-search-close');

    trigger?.addEventListener('focus', this.open.bind(this));
    close?.addEventListener('click', this.close.bind(this));
    form?.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input?.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );
    this.input?.addEventListener('focus', this.onFocus.bind(this));
  }

  /**
   * Get search query.
   *
   * @returns {String}
   */
  getQuery() {
    return this.input.value.trim();
  }

  /**
   * Check if has search query.
   *
   * @returns {Boolean}
   */
  hasQuery() {
    return !!this.getQuery().length;
  }

  /**
   * Handle input change.
   *
   * @returns {Void}
   */
  onChange() {
    const searchTerm = this.getQuery();

    if (!this.hasQuery()) {
      this.close(true);
      this.clearSearchResults();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  /**
   * Handle form submit.
   *
   * @param {Event} event
   */
  onFormSubmit(event) {
    if (!this.hasQuery() || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  /**
   * Handle input focus.
   *
   * @returns {Void}
   */
  onFocus() {
    const searchTerm = this.getQuery();

    if (!this.hasQuery()) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  /**
   * Handle input focus out.
   */
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  /**
   * Get search results.
   *
   * @param {String} searchTerm
   * @returns {Void}
   */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm,
      )}&${encodeURIComponent(
        'resources[type]',
      )}=product,collection&${encodeURIComponent(
        'resources[limit]',
      )}=10&${encodeURIComponent('resources[limit_scope]')}=each&section_id=${
        this.section
      }`,
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${this.section}`).innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  /**
   * Populate search results.
   *
   * @param {String} html
   */
  populateSearchResults(html = '') {
    this.predictiveSearchResults.innerHTML = html;
  }

  /**
   * Populate mobile search results.
   *
   * @param {String} html
   */
  populateMobileSearchResults(html = '') {
    const markup = new DOMParser().parseFromString(html, 'text/html');
    const productsContainer = this.querySelector(
      'predictive-search-results[data-products]',
    );
    const collectionsContainer = this.querySelector(
      'predictive-search-results[data-collections]',
    );
    const productsSource = markup.querySelector('[data-products]')?.innerHTML;
    const collectionsSource =
      markup.querySelector('[data-collections]')?.innerHTML;

    if (productsContainer && productsSource) {
      productsContainer.innerHTML = productsSource;
    }

    if (collectionsContainer) {
      collectionsContainer.innerHTML = collectionsSource;
    }
  }

  /**
   * Render search results.
   *
   * @param {HTMLElement} resultsMarkup
   */
  renderSearchResults(resultsMarkup) {
    if (this.isMobile) {
      this.populateMobileSearchResults(resultsMarkup);
    } else {
      this.populateSearchResults(resultsMarkup);
      this.open();
    }
  }

  /**
   * Clear search results.
   */
  clearSearchResults() {
    this.querySelectorAll('predictive-search-results').forEach(
      (results) => (results.innerHTML = ''),
    );
  }

  /**
   * Open menu.
   */
  open() {
    this.modal?.classList.add('open');
    this.input?.setAttribute('aria-expanded', true);
    this.input?.focus();
    bodyScroll?.lock(this.modal);
  }

  /**
   * Close menu.
   *
   * @param {Boolean} clearSearchTerm
   */
  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.modal.classList.remove('open');
      bodyScroll.clear();
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.modal.classList.remove('open');
    this.input.setAttribute('aria-expanded', false);
  }
}

customElements.define('predictive-search', PredictiveSearch);
